import type { Aabb } from "@townsy/contracts";
import { SPORTS_BASKETBALL, SPORTS_SOCCER, SPORTS_TENNIS, type TennisCourt } from "@townsy/world";

/**
 * Canvas painters for the Townsy Sports Center (ADR-0016): court surfaces with their lines, the net pattern, the "AI"
 * badge and the scoreboards. Original artwork, drawn from the shared geometry in @townsy/world, so the lines are where
 * the game's rules are (the 3-point arc, the goal mouth, the service lines).
 *
 * A surface canvas is a top-down map of a rectangle in world metres: north (+z) up, east (+x) right, `ppm` pixels per
 * metre (the 3D maps it onto a ground plane the same way).
 */
export type Ctx = CanvasRenderingContext2D;

export interface SurfaceMap {
  rect: Aabb;
  ppm: number;
  width: number;
  height: number;
}

export function surfaceMap(rect: Aabb, ppm: number): SurfaceMap {
  return { rect, ppm, width: Math.round((rect.maxX - rect.minX) * ppm), height: Math.round((rect.maxZ - rect.minZ) * ppm) };
}

const px = (m: SurfaceMap, x: number) => (x - m.rect.minX) * m.ppm;
const py = (m: SurfaceMap, z: number) => (m.rect.maxZ - z) * m.ppm;

function rectLine(c: Ctx, m: SurfaceMap, b: Aabb, lw: number): void {
  c.lineWidth = lw * m.ppm;
  c.strokeRect(px(m, b.minX), py(m, b.maxZ), (b.maxX - b.minX) * m.ppm, (b.maxZ - b.minZ) * m.ppm);
}

function line(c: Ctx, m: SurfaceMap, x0: number, z0: number, x1: number, z1: number, lw: number): void {
  c.lineWidth = lw * m.ppm;
  c.beginPath();
  c.moveTo(px(m, x0), py(m, z0));
  c.lineTo(px(m, x1), py(m, z1));
  c.stroke();
}

/** Circle (or arc between world angles a0..a1, measured from +x toward +z) around (x, z). */
function circle(c: Ctx, m: SurfaceMap, x: number, z: number, r: number, lw: number, a0 = 0, a1 = Math.PI * 2): void {
  c.lineWidth = lw * m.ppm;
  c.beginPath();
  // Canvas y grows southward, so world angle a maps to canvas angle -a.
  c.arc(px(m, x), py(m, z), r * m.ppm, -a1, -a0);
  c.stroke();
}

function fillRect(c: Ctx, m: SurfaceMap, b: Aabb, color: string): void {
  c.fillStyle = color;
  c.fillRect(px(m, b.minX), py(m, b.maxZ), (b.maxX - b.minX) * m.ppm, (b.maxZ - b.minZ) * m.ppm);
}

/** Small deterministic pseudo-random for texture grain. */
function grain(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export const SPORTS_COLORS = {
  hallFloor: "#AEB8C4",
  hallFloorAlt: "#A6B0BD",
  courtBorder: "#24467E",
  maple: "#D9A867",
  mapleAlt: "#CF9C5C",
  paint: "#2E5AA0",
  line: "#FFFFFF",
  turf: "#4E9A45",
  turfAlt: "#468F3E",
  turfBorder: "#3A7432",
  tennisCourt: "#2F6CB0",
  tennisSurround: "#3D8160",
  scoreBg: "#121826",
  scoreText: "#FFE08A",
  scoreTitle: "#2851D9",
} as const;

/** Sports hall floor tile (repeats every few metres): a calm grey with a light speckle. */
export function paintHallFloor(c: Ctx, size: number): void {
  c.fillStyle = SPORTS_COLORS.hallFloor;
  c.fillRect(0, 0, size, size);
  const r = grain(5);
  for (let i = 0; i < 1400; i++) {
    c.fillStyle = r() < 0.5 ? SPORTS_COLORS.hallFloorAlt : "#B6C0CB";
    c.fillRect(Math.floor(r() * size), Math.floor(r() * size), 2, 2);
  }
  // Faint seams of the floor panels.
  c.strokeStyle = "rgba(60, 72, 90, 0.18)";
  c.lineWidth = 2;
  c.strokeRect(1, 1, size - 2, size - 2);
}

/** The basketball court plane: the court plus a 1 m painted border. */
export const BASKETBALL_SURFACE: Aabb = {
  minX: SPORTS_BASKETBALL.court.minX - 1,
  minZ: SPORTS_BASKETBALL.court.minZ - 1,
  maxX: SPORTS_BASKETBALL.court.maxX + 1,
  maxZ: SPORTS_BASKETBALL.court.maxZ + 1,
};

export function paintBasketball(c: Ctx, m: SurfaceMap): void {
  const B = SPORTS_BASKETBALL;
  const court = B.court;
  fillRect(c, m, m.rect, SPORTS_COLORS.courtBorder);
  fillRect(c, m, court, SPORTS_COLORS.maple);
  // Maple planks along the court.
  const r = grain(17);
  for (let x = court.minX; x < court.maxX; x += 0.24) {
    c.fillStyle = r() < 0.5 ? SPORTS_COLORS.mapleAlt : "#D5A262";
    c.globalAlpha = 0.55;
    c.fillRect(px(m, x), py(m, court.maxZ), 0.24 * m.ppm - 1, (court.maxZ - court.minZ) * m.ppm);
    c.globalAlpha = 1;
    // Plank joints.
    for (let z = court.minZ + r() * 3; z < court.maxZ; z += 2.4 + r() * 1.2) {
      c.fillStyle = "rgba(120, 80, 40, 0.25)";
      c.fillRect(px(m, x), py(m, z), 0.24 * m.ppm - 1, 2);
    }
  }
  // Painted keys and the centre circle.
  for (const h of B.hoops) {
    const dir = h.facesNorth ? 1 : -1;
    const z0 = h.baselineZ;
    const z1 = h.baselineZ + dir * B.freeThrowFromBaseline;
    fillRect(c, m, { minX: h.x - B.keyWidth / 2, maxX: h.x + B.keyWidth / 2, minZ: Math.min(z0, z1), maxZ: Math.max(z0, z1) }, SPORTS_COLORS.paint);
  }
  c.fillStyle = SPORTS_COLORS.paint;
  c.beginPath();
  c.arc(px(m, B.centre.x), py(m, B.centre.z), B.centreCircleRadius * m.ppm, 0, Math.PI * 2);
  c.fill();
  c.strokeStyle = SPORTS_COLORS.line;
  const lw = 0.06;
  rectLine(c, m, court, lw);
  line(c, m, court.minX, B.centre.z, court.maxX, B.centre.z, lw);
  circle(c, m, B.centre.x, B.centre.z, B.centreCircleRadius, lw);
  for (const h of B.hoops) {
    const dir = h.facesNorth ? 1 : -1;
    const ftZ = h.baselineZ + dir * B.freeThrowFromBaseline;
    rectLine(c, m, { minX: h.x - B.keyWidth / 2, maxX: h.x + B.keyWidth / 2, minZ: Math.min(h.baselineZ, ftZ), maxZ: Math.max(h.baselineZ, ftZ) }, lw);
    // Free-throw circle (half toward the court).
    circle(c, m, h.x, ftZ, 1.8, lw, h.facesNorth ? 0 : Math.PI, h.facesNorth ? Math.PI : Math.PI * 2);
    // Three-point arc: the circle of 6.75 m around the hoop, inside the court.
    const R = B.threePointRadius;
    const dz = Math.abs(h.z - h.baselineZ);
    const a = Math.asin(dz / R);
    if (h.facesNorth) circle(c, m, h.x, h.z, R, lw, -a, Math.PI + a);
    else circle(c, m, h.x, h.z, R, lw, Math.PI - a, Math.PI * 2 + a);
    // Restricted area.
    circle(c, m, h.x, h.z, 1.25, lw, h.facesNorth ? 0 : Math.PI, h.facesNorth ? Math.PI : Math.PI * 2);
  }
  // Centre mark: the Townsy name across the circle.
  c.save();
  c.translate(px(m, B.centre.x), py(m, B.centre.z));
  c.fillStyle = "#FFFFFF";
  c.font = `800 ${0.62 * m.ppm}px system-ui, "Segoe UI", sans-serif`;
  c.textAlign = "center";
  c.textBaseline = "middle";
  c.fillText("TOWNSY", 0, 0);
  c.restore();
}

/** The soccer pitch plane: the pitch plus a 1 m border. */
export const SOCCER_SURFACE: Aabb = {
  minX: SPORTS_SOCCER.pitch.minX - 1,
  minZ: SPORTS_SOCCER.pitch.minZ - 1,
  maxX: SPORTS_SOCCER.pitch.maxX + 1,
  maxZ: SPORTS_SOCCER.pitch.maxZ + 1,
};

export function paintSoccer(c: Ctx, m: SurfaceMap): void {
  const S = SPORTS_SOCCER;
  const p = S.pitch;
  fillRect(c, m, m.rect, SPORTS_COLORS.turfBorder);
  // Mown stripes across the pitch (4 m).
  let k = 0;
  for (let z = p.minZ; z < p.maxZ; z += 4, k++) fillRect(c, m, { minX: p.minX, maxX: p.maxX, minZ: z, maxZ: Math.min(p.maxZ, z + 4) }, k % 2 ? SPORTS_COLORS.turfAlt : SPORTS_COLORS.turf);
  const r = grain(23);
  for (let i = 0; i < 9000; i++) {
    c.fillStyle = r() < 0.5 ? "rgba(30, 70, 25, 0.22)" : "rgba(140, 200, 110, 0.18)";
    c.fillRect(r() * m.width, r() * m.height, 2, 2);
  }
  c.strokeStyle = SPORTS_COLORS.line;
  const lw = 0.1;
  rectLine(c, m, p, lw);
  line(c, m, p.minX, S.halfwayZ, p.maxX, S.halfwayZ, lw);
  circle(c, m, S.centreX, S.halfwayZ, S.centreCircleRadius, lw);
  rectLine(c, m, S.penaltyArea, lw);
  rectLine(c, m, S.goalArea, lw);
  // The arc outside the penalty area, 3 m around the spot.
  const d = S.penaltySpot.z - S.penaltyArea.minZ;
  const a = Math.acos(Math.min(1, d / 3));
  circle(c, m, S.penaltySpot.x, S.penaltySpot.z, 3, lw, -Math.PI / 2 - a, -Math.PI / 2 + a);
  c.fillStyle = SPORTS_COLORS.line;
  for (const spot of [S.penaltySpot, { x: S.centreX, z: S.halfwayZ }]) {
    c.beginPath();
    c.arc(px(m, spot.x), py(m, spot.z), 0.14 * m.ppm, 0, Math.PI * 2);
    c.fill();
  }
  // The goal line inside the mouth, a little thicker.
  line(c, m, S.goal.x - S.goal.halfWidth, S.goal.lineZ, S.goal.x + S.goal.halfWidth, S.goal.lineZ, 0.14);
}

/** A tennis court plane (court + run-off), in the court's own frame (same for both courts). */
export function tennisSurface(court: TennisCourt): Aabb {
  return {
    minX: court.court.minX - SPORTS_TENNIS.runoffSide,
    maxX: court.court.maxX + SPORTS_TENNIS.runoffSide,
    minZ: court.court.minZ - SPORTS_TENNIS.runoffBack,
    maxZ: court.court.maxZ + SPORTS_TENNIS.runoffBack,
  };
}

export function paintTennis(c: Ctx, m: SurfaceMap, court: TennisCourt): void {
  const T = SPORTS_TENNIS;
  const k = court.court;
  fillRect(c, m, m.rect, SPORTS_COLORS.tennisSurround);
  fillRect(c, m, k, SPORTS_COLORS.tennisCourt);
  const r = grain(31);
  for (let i = 0; i < 5000; i++) {
    c.fillStyle = r() < 0.5 ? "rgba(0, 0, 0, 0.07)" : "rgba(255, 255, 255, 0.06)";
    c.fillRect(r() * m.width, r() * m.height, 2, 2);
  }
  c.strokeStyle = SPORTS_COLORS.line;
  const lw = 0.06;
  rectLine(c, m, k, lw);
  const sx0 = k.minX + T.singlesInset;
  const sx1 = k.maxX - T.singlesInset;
  line(c, m, sx0, k.minZ, sx0, k.maxZ, lw);
  line(c, m, sx1, k.minZ, sx1, k.maxZ, lw);
  for (const dir of [-1, 1]) {
    const sz = court.netZ + dir * T.serviceLineFromNet;
    line(c, m, sx0, sz, sx1, sz, lw);
    // Centre mark on each baseline.
    const bz = dir < 0 ? k.minZ : k.maxZ;
    line(c, m, court.centreX, bz, court.centreX, bz - dir * 0.2, lw);
  }
  line(c, m, court.centreX, court.netZ - T.serviceLineFromNet, court.centreX, court.netZ + T.serviceLineFromNet, lw);
  // Court number in the run-off behind the south baseline.
  c.fillStyle = "rgba(255, 255, 255, 0.85)";
  c.font = `800 ${1.4 * m.ppm}px system-ui, "Segoe UI", sans-serif`;
  c.textAlign = "center";
  c.textBaseline = "middle";
  c.fillText(String(court.id + 1), px(m, court.centreX), py(m, k.minZ - 3.2));
}

/** Net mesh pattern: transparent with light cords (tinted by the material). */
export function paintNet(c: Ctx, size: number, cells: number): void {
  c.clearRect(0, 0, size, size);
  c.strokeStyle = "rgba(255, 255, 255, 1)";
  c.lineWidth = Math.max(2, size / 40);
  const step = size / cells;
  c.beginPath();
  for (let i = 0; i <= cells; i++) {
    const v = i * step;
    c.moveTo(v, 0);
    c.lineTo(v, size);
    c.moveTo(0, v);
    c.lineTo(size, v);
  }
  c.stroke();
}

/** The floating "AI" badge above the computer players. */
export function paintAiBadge(c: Ctx, w: number, h: number, subtitle: string): void {
  c.clearRect(0, 0, w, h);
  const r = h * 0.22;
  c.fillStyle = "#2851D9";
  c.beginPath();
  c.moveTo(r, 0);
  c.lineTo(w - r, 0);
  c.quadraticCurveTo(w, 0, w, r);
  c.lineTo(w, h - r);
  c.quadraticCurveTo(w, h, w - r, h);
  c.lineTo(r, h);
  c.quadraticCurveTo(0, h, 0, h - r);
  c.lineTo(0, r);
  c.quadraticCurveTo(0, 0, r, 0);
  c.fill();
  c.fillStyle = "#FFFFFF";
  c.textAlign = "center";
  c.textBaseline = "middle";
  c.font = `800 ${h * 0.5}px system-ui, "Segoe UI", sans-serif`;
  c.fillText("AI", w / 2, h * 0.4);
  c.font = `600 ${h * 0.2}px system-ui, "Segoe UI", sans-serif`;
  c.fillText(subtitle, w / 2, h * 0.8);
}

export interface ScoreboardText {
  title: string;
  lines: string[];
}

/** A scoreboard face: a title bar and up to four lines of large, high-contrast text. */
export function paintScoreboard(c: Ctx, w: number, h: number, t: ScoreboardText): void {
  c.fillStyle = SPORTS_COLORS.scoreBg;
  c.fillRect(0, 0, w, h);
  const bar = Math.round(h * 0.24);
  c.fillStyle = SPORTS_COLORS.scoreTitle;
  c.fillRect(0, 0, w, bar);
  c.fillStyle = "#FFFFFF";
  c.textAlign = "center";
  c.textBaseline = "middle";
  c.font = `800 ${bar * 0.6}px system-ui, "Segoe UI", sans-serif`;
  c.fillText(t.title, w / 2, bar / 2, w * 0.94);
  const lines = t.lines.slice(0, 4);
  const rowH = (h - bar) / Math.max(3, lines.length);
  lines.forEach((l, i) => {
    c.fillStyle = i === 0 ? SPORTS_COLORS.scoreText : "#E8ECF4";
    c.font = `${i === 0 ? 800 : 600} ${rowH * (i === 0 ? 0.62 : 0.5)}px system-ui, "Segoe UI", sans-serif`;
    c.fillText(l, w / 2, bar + rowH * (i + 0.5), w * 0.94);
  });
  c.strokeStyle = "#3A4458";
  c.lineWidth = Math.max(4, w / 120);
  c.strokeRect(0, 0, w, h);
}

/** The lobby kiosk's directory board (facing the entrance): where each sport is. */
export function paintDirectory(c: Ctx, w: number, h: number, live: string[]): void {
  c.fillStyle = "#FBF8F2";
  c.fillRect(0, 0, w, h);
  const bar = h * 0.2;
  c.fillStyle = "#1B1F2A";
  c.fillRect(0, 0, w, bar);
  c.fillStyle = "#FFFFFF";
  c.textAlign = "center";
  c.textBaseline = "middle";
  c.font = `800 ${bar * 0.5}px system-ui, "Segoe UI", sans-serif`;
  c.fillText("TOWNSY SPORTS CENTER", w / 2, bar / 2, w * 0.94);
  const rows: Array<[string, string, string]> = [
    ["←", "Soccer", "vs the AI goalie"],
    ["↑", "Basketball", "shoot, pass, steal"],
    ["→", "Tennis", "2 courts, people or AI"],
  ];
  const rowH = (h - bar) * 0.2;
  rows.forEach(([arrow, name, sub], i) => {
    const y = bar + rowH * (i + 0.6);
    c.fillStyle = "#2851D9";
    c.font = `800 ${rowH * 0.7}px system-ui, "Segoe UI", sans-serif`;
    c.textAlign = "center";
    c.fillText(arrow, w * 0.1, y);
    c.textAlign = "left";
    c.fillStyle = "#1B1F2A";
    c.font = `800 ${rowH * 0.5}px system-ui, "Segoe UI", sans-serif`;
    c.fillText(name, w * 0.18, y);
    c.fillStyle = "#4A5062";
    c.font = `600 ${rowH * 0.36}px system-ui, "Segoe UI", sans-serif`;
    c.fillText(sub, w * 0.5, y, w * 0.48);
  });
  c.fillStyle = "#1F7A4D";
  c.textAlign = "center";
  c.font = `700 ${rowH * 0.36}px system-ui, "Segoe UI", sans-serif`;
  const liveTop = bar + rowH * 3.25;
  const lines = live.length ? live.slice(0, 2) : ["Free to play. Scores last for your visit."];
  lines.forEach((l, i) => c.fillText(l, w / 2, liveTop + i * rowH * 0.55, w * 0.92));
}
